// eslint-disable-next-line @github-ui/github-monorepo/filename-convention
import {useCallback, useEffect, useEffectEvent, useRef} from 'react'
import {useIntersectionObserver} from '@github-ui/swp-core/lib/hooks/use-intersection-observer'

export interface VideoRef {
  videoRef: React.RefObject<HTMLVideoElement | null>
  isPaused: boolean
}

const useVideo = ({videoRef, isPaused}: VideoRef) => {
  const videoSrc = useRef<string>('')
  const currentVideoTime = useRef<number>(-1)

  const {isIntersecting} = useIntersectionObserver(videoRef, {threshold: 0.33, isOnce: true})

  const play = useCallback(async () => {
    if (videoRef.current?.readyState && videoRef.current?.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
      try {
        await videoRef.current.play()
      } catch (error: unknown) {
        // Autoplay was prevented
        if (error instanceof Error && error.name === 'NotAllowedError') {
          // Try playing muted
          try {
            videoRef.current.muted = true
            await videoRef.current.play()
          } catch {
            // Even muted playback failed, ignore
          }
        }
      }
    }
  }, [videoRef])

  const addListeners = useCallback(() => {
    if (videoRef.current) {
      videoRef.current.addEventListener('canplay', play)

      // Safari won't load without an explicit statement
      videoRef.current.load()
    }
  }, [play, videoRef])

  const removeListeners = useCallback(() => {
    if (videoRef.current) {
      videoRef.current.removeEventListener('canplay', play)
    }
  }, [play, videoRef])

  const onVideoTimeUpdate = useCallback((event: React.SyntheticEvent<HTMLVideoElement>) => {
    const video = event.currentTarget
    if (!video) return

    // Resync the video if we just switched desktop/mobile assets
    if (videoSrc.current !== video.src && currentVideoTime.current >= 0) {
      video.currentTime = currentVideoTime.current
    }

    videoSrc.current = video.src || ''
    currentVideoTime.current = video.currentTime || 0
  }, [])

  const toggle = useEffectEvent(() => {
    if (videoRef.current?.readyState && videoRef.current?.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
      const method = isPaused ? 'pause' : 'play'
      try {
        videoRef.current[method]()
      } catch {
        // Ignore errors
      }
    }
  })

  useEffect(() => {
    toggle()
  }, [isPaused])

  useEffect(() => {
    if (isIntersecting && !isPaused) {
      if (videoRef.current?.readyState && videoRef.current?.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) play()
      else addListeners()
    }
  }, [isIntersecting, addListeners, isPaused, play, videoRef])

  return {
    addListeners,
    removeListeners,
    onVideoTimeUpdate,
  }
}

export default useVideo
